/**
 * The single active-call state machine for the web client. One call at a time.
 *
 * States: idle, ringing-out, ringing-in, connecting, active, ended. This module
 * is the sole owner of transitions, timers, and the media client. UI surfaces
 * (incoming-call modal, in-call overlay, thread call buttons) subscribe to it
 * and call its methods. Socket events are translated into method calls by
 * index.js.
 *
 * Timing constants are one authoritative set shared with the backend contract:
 * a 40s ring and a 15s media-connection watchdog (README "Ring timing" and
 * "Media-connection failure").
 */

import { callApi, CallApiError } from './call-api.js';
import { ringTone } from './ring-tone.js';

const RING_DURATION_MS = 40000;
const MEDIA_CONNECT_TIMEOUT_MS = 15000;

export const CallStatus = {
    IDLE: 'idle',
    RINGING_OUT: 'ringing-out',
    RINGING_IN: 'ringing-in',
    CONNECTING: 'connecting',
    ACTIVE: 'active',
    ENDED: 'ended',
};

const EMPTY_AVATAR = '//ipstatic.net/images/male-empty-profile.png';

function avatarUrl(user) {
    if (!user) {
        return EMPTY_AVATAR;
    }
    const avatar = user.avatar || {};
    return avatar.url || avatar.thumb_small || avatar.thumb || EMPTY_AVATAR;
}

function normalizeUser(user) {
    if (!user) {
        return null;
    }
    return {
        id: String(user.id || ''),
        username: user.username || '',
        name: user.name || user.username || '',
        avatar: avatarUrl(user),
    };
}

class CallState {
    constructor() {
        this.status = CallStatus.IDLE;
        this.call = null;
        this.callId = null;
        this.role = null;
        this.type = null;
        this.other = null;
        this.livekit = null;
        this.muted = false;
        this.cameraOff = false;
        this.startedAt = null;
        this.endReason = null;

        this.client = null;
        this._placing = false;
        this._seenVersion = new Map();
        this._ringTimer = null;
        this._connectTimer = null;
        this._changeListeners = new Set();
        this._eventListeners = new Map();
    }

    // --- subscription -----------------------------------------------------

    subscribe(cb) {
        this._changeListeners.add(cb);
        return () => this._changeListeners.delete(cb);
    }

    on(event, cb) {
        if (!this._eventListeners.has(event)) {
            this._eventListeners.set(event, new Set());
        }
        this._eventListeners.get(event).add(cb);
        return () => this._eventListeners.get(event)?.delete(cb);
    }

    _emitChange() {
        const snap = this.snapshot();
        this._changeListeners.forEach((cb) => {
            try {
                cb(snap);
            } catch (e) {
                /* a listener error must not break the state machine */
            }
        });
    }

    _emit(event, detail) {
        this._eventListeners.get(event)?.forEach((cb) => {
            try {
                cb(detail);
            } catch (e) {
                /* isolate listener errors */
            }
        });
    }

    snapshot() {
        return {
            status: this.status,
            callId: this.callId,
            role: this.role,
            type: this.type,
            other: this.other,
            muted: this.muted,
            cameraOff: this.cameraOff,
            startedAt: this.startedAt,
            endReason: this.endReason,
        };
    }

    isBusy() {
        return this.status !== CallStatus.IDLE && this.status !== CallStatus.ENDED;
    }

    // --- dedup / ordering -------------------------------------------------

    /**
     * Drop a socket event whose state_version is not newer than the latest
     * seen for its call. Events with no version (authorization events) are
     * handled elsewhere and never reach here.
     */
    _isStale(callId, version) {
        if (callId === null || callId === undefined || version === undefined || version === null) {
            return false;
        }
        const key = String(callId);
        const seen = this._seenVersion.get(key);
        const next = Number(version);
        if (seen !== undefined && next <= seen) {
            return true;
        }
        this._seenVersion.set(key, next);
        return false;
    }

    // --- outgoing (caller) ------------------------------------------------

    async startOutgoing(targetId, type, other) {
        if (this.isBusy() || this._placing) {
            return;
        }

        // Place the call before showing any UI, so a rejected create (busy,
        // rate limit, gating) surfaces as an error toast with no overlay flash.
        this._placing = true;
        let result;
        try {
            result = await callApi.createCall(targetId, type);
        } finally {
            this._placing = false;
        }

        this.status = CallStatus.RINGING_OUT;
        this.role = 'caller';
        this.type = type;
        this.other = normalizeUser(other) || { id: String(targetId), username: '', name: '', avatar: EMPTY_AVATAR };
        this.call = result;
        this.callId = String(result.id);
        this.muted = false;
        this.cameraOff = false;
        this.startedAt = null;
        this.endReason = null;
        this._isStale(this.callId, result.state_version);

        // A 201 create carries fresh join credentials. A 200 idempotent replay of
        // an existing pending call does not, so fetch a token to (re)join.
        let credentials = result.livekit || null;
        if (!credentials) {
            try {
                const token = await callApi.getToken(this.callId);
                credentials = token.livekit || null;
            } catch (e) {
                credentials = null;
            }
        }

        if (this.status !== CallStatus.RINGING_OUT || this.callId !== String(result.id)) {
            // The call was already resolved (declined/canceled) while we awaited.
            return;
        }

        this.livekit = credentials;
        this._startRingTimer(() => this._onOutgoingTimeout());
        ringTone.startOutgoing();
        this._emitChange();

        this._joinRoom({ isRingback: true });
    }

    _onOutgoingTimeout() {
        if (this.status !== CallStatus.RINGING_OUT) {
            return;
        }
        if (this.callId) {
            callApi.cancelCall(this.callId).catch(() => {});
        }
        this._finish('unanswered', { toast: 'no_answer' });
    }

    // --- incoming (callee) ------------------------------------------------

    incomingRing(call, caller, livekitUrl) {
        if (!call || !call.id) {
            return;
        }
        if (this._isStale(call.id, call.state_version)) {
            return;
        }
        // Already busy with another call: ignore the ring. The backend busy check
        // should prevent this, but a late socket must never hijack a live call.
        if (this.isBusy() && String(call.id) !== this.callId) {
            return;
        }

        this.status = CallStatus.RINGING_IN;
        this.role = 'callee';
        this.type = call.type;
        this.call = call;
        this.callId = String(call.id);
        this.other = normalizeUser(caller);
        this.livekit = { url: livekitUrl || '', token: null };
        this.muted = false;
        this.cameraOff = false;
        this.startedAt = null;
        this.endReason = null;

        this._startRingTimer(() => this._finish('unanswered', { toast: 'missed' }));
        ringTone.startIncoming();
        this._emitChange();
    }

    async accept() {
        if (this.status !== CallStatus.RINGING_IN || !this.callId) {
            return;
        }
        this._clearRingTimer();
        ringTone.stop();
        this.status = CallStatus.CONNECTING;
        this._emitChange();

        let result;
        try {
            result = await callApi.acceptCall(this.callId);
        } catch (error) {
            this._finish('error', { toast: this._errorToast(error) });
            return;
        }

        this.call = result;
        this.livekit = result.livekit || this.livekit;
        this._startConnectWatchdog();
        this._emitChange();
        this._joinRoom({ isRingback: false });
    }

    decline() {
        if (this.status !== CallStatus.RINGING_IN || !this.callId) {
            return;
        }
        const id = this.callId;
        callApi.declineCall(id).catch(() => {});
        this._finish('declined-local', null);
    }

    // --- media join -------------------------------------------------------

    async _joinRoom({ isRingback }) {
        const credentials = this.livekit;
        if (!credentials || !credentials.url || !credentials.token) {
            if (isRingback) {
                // Caller could not obtain a token: give up cleanly.
                if (this.callId) {
                    callApi.cancelCall(this.callId).catch(() => {});
                }
                this._finish('error', { toast: 'unavailable' });
            } else {
                this._connectFailed();
            }
            return;
        }

        const wantVideo = this.type === 'video';
        try {
            const { CallClient } = await import(/* webpackChunkName: "livekit" */ './call-client.js');
            if (this.callId === null) {
                return;
            }
            this.client = new CallClient({
                onRemoteTrack: (track) => this._emit('remote-track', { track }),
                onLocalVideoTrack: (track) => this._emit('local-video-track', { track }),
                onTrackRemoved: (track) => this._emit('track-removed', { track }),
                onPeerConnected: () => this._onPeerConnected(),
                onPeerDisconnected: () => this._onPeerDisconnected(),
                onDisconnected: () => this._onRoomDisconnected(),
            });
            await this.client.connect(credentials.url, credentials.token, { video: wantVideo });
            this.cameraOff = wantVideo ? false : true;

            // The other party may already be in the room (the caller always joins
            // first), so reconcile immediately after connect.
            if (this.client.hasRemoteParticipant()) {
                this._onPeerConnected();
            }
            this._emitChange();
        } catch (error) {
            if (isRingback && this.status === CallStatus.RINGING_OUT) {
                // Caller's own ringback never connected: cancel, do not attribute
                // connect_failed (that is a post-accept reason only).
                if (this.callId) {
                    callApi.cancelCall(this.callId).catch(() => {});
                }
                this._finish('error', { toast: 'unavailable' });
            } else {
                this._connectFailed();
            }
        }
    }

    _onPeerConnected() {
        this._clearConnectWatchdog();
        this._clearRingTimer();
        if (this.status === CallStatus.RINGING_OUT || this.status === CallStatus.CONNECTING) {
            this.status = CallStatus.ACTIVE;
            this.startedAt = Date.now();
            ringTone.stop();
            this._emitChange();
        }
    }

    _onPeerDisconnected() {
        // The other side left or crashed: end the call from our side (the fast
        // teardown path).
        if (this.status === CallStatus.ACTIVE || this.status === CallStatus.CONNECTING) {
            if (this.callId) {
                callApi.endCall(this.callId, 'normal').catch(() => {});
            }
            this._finish('remote-ended', { toast: 'ended' });
        }
    }

    _onRoomDisconnected() {
        if (this.status === CallStatus.ACTIVE || this.status === CallStatus.CONNECTING) {
            this._finish('remote-ended', { toast: 'ended' });
        }
    }

    // --- caller signals ---------------------------------------------------

    remoteAccepted(callId, version) {
        if (this.role !== 'caller' || this.callId !== String(callId)) {
            return;
        }
        if (this._isStale(callId, version)) {
            return;
        }
        // The callee is now present. Media may still be settling: start the
        // watchdog and let peer-connected promote us to active.
        if (this.status === CallStatus.RINGING_OUT) {
            this.status = CallStatus.CONNECTING;
            this._startConnectWatchdog();
            this._emitChange();
        }
    }

    remoteDeclined(callId, version) {
        if (this.callId !== String(callId) || this._isStale(callId, version)) {
            return;
        }
        this._finish('declined', { toast: 'declined' });
    }

    remoteCanceled(callId, version, reason) {
        if (this.callId !== String(callId) || this._isStale(callId, version)) {
            return;
        }
        // The callee sees a caller-cancel or ring timeout as a missed call. The
        // caller only receives a cancel when the system tears its ring down (for
        // example an authorization revoked mid-ring).
        const toast = this.role === 'callee' ? 'missed' : 'canceled';
        this._finish('canceled', { toast });
    }

    answeredElsewhere(callId, version) {
        if (this.callId !== String(callId) || this._isStale(callId, version)) {
            return;
        }
        this._finish('answered-elsewhere', null);
    }

    remoteEnded(callId, version, duration) {
        if (this.callId !== String(callId) || this._isStale(callId, version)) {
            return;
        }
        this._finish('remote-ended', { toast: 'ended', duration });
    }

    // --- in-call controls -------------------------------------------------

    hangUp() {
        if (!this.callId) {
            this._finish('normal', null);
            return;
        }
        if (this.status === CallStatus.RINGING_OUT) {
            callApi.cancelCall(this.callId).catch(() => {});
        } else {
            callApi.endCall(this.callId, 'normal').catch(() => {});
        }
        this._finish('normal', null);
    }

    async toggleMute() {
        if (!this.client) {
            return;
        }
        this.muted = await this.client.setMuted(!this.muted);
        this._emitChange();
    }

    async toggleCamera() {
        if (!this.client || this.type !== 'video') {
            return;
        }
        this.cameraOff = !(await this.client.setCameraEnabled(this.cameraOff));
        this._emitChange();
    }

    async switchCamera() {
        if (this.client) {
            await this.client.switchCamera();
        }
    }

    canSwitchCamera() {
        return !!(this.client && this.client.canSwitchCamera());
    }

    // --- watchdogs / timers ----------------------------------------------

    _startRingTimer(onExpire) {
        this._clearRingTimer();
        this._ringTimer = setTimeout(onExpire, RING_DURATION_MS);
    }

    _clearRingTimer() {
        if (this._ringTimer) {
            clearTimeout(this._ringTimer);
            this._ringTimer = null;
        }
    }

    _startConnectWatchdog() {
        this._clearConnectWatchdog();
        this._connectTimer = setTimeout(() => this._connectFailed(), MEDIA_CONNECT_TIMEOUT_MS);
    }

    _clearConnectWatchdog() {
        if (this._connectTimer) {
            clearTimeout(this._connectTimer);
            this._connectTimer = null;
        }
    }

    _connectFailed() {
        if (this.status === CallStatus.ACTIVE) {
            return;
        }
        if (this.callId) {
            callApi.endCall(this.callId, 'connect_failed').catch(() => {});
        }
        this._finish('connect-failed', { toast: 'connect_failed' });
    }

    // --- teardown ---------------------------------------------------------

    _finish(reason, notify) {
        this.endReason = reason;
        this._teardownMedia();
        this._clearRingTimer();
        this._clearConnectWatchdog();
        ringTone.stop();

        const summary = {
            reason,
            role: this.role,
            type: this.type,
            other: this.other,
            duration: notify && notify.duration ? Number(notify.duration) : this._elapsedSeconds(),
            toast: notify ? notify.toast : null,
        };

        this.status = CallStatus.ENDED;
        this._emitChange();
        this._emit('ended', summary);

        this._resetToIdle();
    }

    _resetToIdle() {
        this.status = CallStatus.IDLE;
        this.call = null;
        this.callId = null;
        this.role = null;
        this.type = null;
        this.other = null;
        this.livekit = null;
        this.muted = false;
        this.cameraOff = false;
        this.startedAt = null;
        this._emitChange();
    }

    _teardownMedia() {
        if (this.client) {
            try {
                this.client.disconnect();
            } catch (e) {
                /* ignore */
            }
            this.client = null;
        }
    }

    _elapsedSeconds() {
        return this.startedAt ? Math.max(0, Math.round((Date.now() - this.startedAt) / 1000)) : 0;
    }

    _errorToast(error) {
        if (error instanceof CallApiError) {
            return this._codeToast(error.code);
        }
        return 'unavailable';
    }

    _codeToast(code) {
        switch (code) {
            case 'call_invalid_state':
                return 'canceled';
            default:
                return 'unavailable';
        }
    }
}

export const callState = new CallState();

export default callState;
